/**
 * Merge fields (MERGE-FIELDS) — the {tokens} a reply template or a dispatcher
 * can drop into a customer message, resolved per-customer at insert time.
 *
 * Smart about the two things Seth flagged:
 *  - BUSINESSES: a company name ("Verde Management") never becomes "Hi Verde,".
 *    The first-name token resolves to "there" for a business; use the Account
 *    name / Full name token when you want to name the company.
 *  - WHO you're texting (rentals): when Podium knows the actual person on the
 *    thread (a tenant, a property manager's rep), the first name prefers THAT
 *    person over the account holder.
 *
 * The insert menu shows the RESOLVED value next to each field, so you always
 * see what it'll become before you drop it in.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Braces, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";

export interface MergeContext {
  firstName: string; // greeting-safe: residential first name, else "there"
  lastName: string;
  fullName: string; // proper display name (First Last) or the business name
  accountName: string; // the account / business name as it reads
  address: string; // service address, line 1
  cityStateZip: string;
  isBusiness: boolean;
}

export interface MergeField {
  token: string;
  label: string;
  pick: (c: MergeContext) => string;
}

export const MERGE_FIELDS: MergeField[] = [
  { token: ":contact_first_name:", label: "First Name", pick: (c) => c.firstName },
  { token: ":contact_last_name:", label: "Last Name", pick: (c) => c.lastName },
  { token: ":contact_full_name:", label: "Full Name", pick: (c) => c.fullName },
  { token: ":account_name:", label: "Account / Business Name", pick: (c) => c.accountName },
  { token: ":service_address:", label: "Service Address", pick: (c) => c.address },
  { token: ":city_state_zip:", label: "City, State, ZIP", pick: (c) => c.cityStateZip },
  { token: ":company:", label: "Our Company (Sedgwick Heating)", pick: () => "Sedgwick Heating" },
];

// Words that mark a name as a business rather than a person.
const BIZ = /\b(LLC|L\.L\.C|INC|CORP|CO|COMPANY|MANAGEMENT|MGMT|PROPERT(?:Y|IES)|APARTMENTS?|APTS?|HOMES?|RENTALS?|REALTY|ASSOCIATION|ASSOC|HOA|CHURCH|SCHOOL|RESTAURANT|CAFE|LLP|GROUP|SERVICES?|ENTERPRISES?|HOLDINGS?|PARTNERS?|BUILDERS?|CONSTRUCTION|CENTER|CLINIC|BANK|STORE|SHOP|FARMS?)\b/i;

function parsePersonName(raw: string): { first: string; last: string; isPerson: boolean } {
  const s = (raw ?? "").replace(/\s+\d{4,}$/, "").trim(); // strip trailing custno
  if (!s) return { first: "", last: "", isPerson: false };
  const comma = s.match(/^([^,]+),\s*(.+)$/); // ESC "Last,First" = residential
  if (comma) {
    return { first: comma[2].trim().split(/\s+/)[0], last: comma[1].trim(), isPerson: true };
  }
  if (BIZ.test(s)) return { first: "", last: "", isPerson: false };
  const words = s.split(/\s+/);
  if (words.length <= 3) return { first: words[0], last: words.slice(1).join(" "), isPerson: true };
  return { first: "", last: "", isPerson: false }; // long, no comma → treat as org
}

interface CustRow {
  fullname: string | null;
  firstname: string | null;
  lastname: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}
interface LocRow {
  add1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** Resolve the merge values for a customer; prefer the actual person texting
 *  (Podium's contact name) for the greeting when it looks like a person. */
export function useCustomerMergeContext(custno: string | null, providerName?: string | null) {
  return useQuery({
    queryKey: ["merge_ctx", custno, providerName ?? ""],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MergeContext> => {
      const [{ data: c }, { data: loc }] = await Promise.all([
        supabase.from("mirror_customer").select("fullname, firstname, lastname, city, state, zip").eq("custno", custno!).maybeSingle(),
        supabase.from("mirror_location").select("add1, city, state, zip").eq("custno", custno!).limit(1).maybeSingle(),
      ]);
      const cust = (c ?? {}) as CustRow;
      const location = (loc ?? {}) as LocRow;
      const account = (cust.fullname ?? "").replace(/\s+\d{4,}$/, "").trim();

      const fromProvider = providerName ? parsePersonName(providerName) : null;
      const fromAccount = parsePersonName(account);
      const person = fromProvider?.isPerson ? fromProvider : fromAccount;
      const isBusiness = !person.isPerson;

      const first = (cust.firstname?.trim() || person.first || "").trim();
      const last = (cust.lastname?.trim() || person.last || "").trim();
      // Clean the ESC "Last,First" comma format for any name we show.
      const cleanAccount = account.includes(",") ? account.split(",").reverse().join(" ").trim() : account;
      const display = isBusiness
        ? cleanAccount
        : first && last
          ? `${first} ${last}`
          : cleanAccount;

      const city = location.city ?? cust.city ?? "";
      const state = location.state ?? cust.state ?? "";
      const zip = location.zip ?? cust.zip ?? "";

      return {
        firstName: isBusiness ? "there" : first || "there",
        lastName: last,
        fullName: display || cleanAccount || "there",
        accountName: cleanAccount,
        address: location.add1 ?? "",
        cityStateZip: [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
        isBusiness,
      };
    },
  });
}

/** Swap every known token for its value; unknown bits are left as-is. */
export function applyMergeFields(text: string, ctx: MergeContext | null | undefined): string {
  if (!ctx) return text.replace(/:contact_first_name:/gi, "there").replace(/:contact:/gi, "there");
  let out = text;
  for (const f of MERGE_FIELDS) out = out.split(f.token).join(f.pick(ctx));
  out = out.split(":contact:").join(ctx.firstName); // Podium alias
  return out;
}

/** Dropdown that inserts a token; shows the resolved value when a customer
 *  context is supplied (so "First name → there" is visible for a business). */
export function MergeFieldMenu({
  ctx,
  onInsert,
  dropUp,
  label = "Insert field",
}: {
  ctx?: MergeContext;
  onInsert: (token: string) => void;
  dropUp?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
        title="Insert a field that fills in the customer's details"
      >
        <Braces className="h-3 w-3" /> {label} <ChevronDown className="h-2.5 w-2.5 text-slate-400" />
      </button>
      {open && (
        <div className={cn("absolute left-0 z-30 w-72 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-e2", dropUp ? "bottom-full mb-1" : "mt-1")}>
          {ctx?.isBusiness && (
            <div className="px-3 py-1.5 text-[11px] text-amber-700">
              This is a business — “First name” becomes “there” so it never reads “Hi {ctx.accountName.split(/\s+/)[0]},”.
            </div>
          )}
          {MERGE_FIELDS.map((f) => {
            const preview = ctx ? f.pick(ctx) : "";
            return (
              <button
                key={f.token}
                type="button"
                onClick={() => {
                  onInsert(f.token);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors hover:bg-slate-50"
              >
                <span className="text-xs font-medium text-slate-700">{f.label}</span>
                <span className="max-w-[45%] truncate text-[11px] text-slate-400">
                  {ctx ? (preview ? `→ ${preview}` : "→ —") : f.token}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
